import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, HStack, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { CatalogueItem } from '../../utils/types';
import {
  deleteCatalogueItem,
  getCatalogue,
  getCatalogueItems,
} from '../../hooks/catalogueItems';
import ConfirmModal from '../../components/ConfirmationModal';
import CatalogueCards from './CatalogueItemCard';
import CatalogueTable from './CatalogueTable';
import {
  CatalogueCommandBar,
  CatalogueCreateMenu,
  CatalogueHero,
  CatalogueListSkeleton,
  CatalogueShell,
  PrimaryCatalogueButton,
  formatCurrency,
} from './components/catalogueUi';

const CataloguePage = () => {
  const [products, setProducts] = useState<CatalogueItem[]>([]);
  const [catalogueId, setCatalogueId] = useState('');
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('updated');
  const [status, setStatus] = useState('all');
  const [view, setView] = useState<'table' | 'grid'>('table');
  const [selectedItem, setSelectedItem] = useState<CatalogueItem | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchProducts = async () => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    try {
      const catalogueResponse = await getCatalogue(orgId);

      if (!catalogueResponse.success || !catalogueResponse.data?.id) {
        throw new Error('Catalogue could not be loaded');
      }

      const resolvedCatalogueId = String(catalogueResponse.data.id);
      setCatalogueId(resolvedCatalogueId);

      const itemsResponse = await getCatalogueItems(orgId, resolvedCatalogueId);

      if (!itemsResponse.success) {
        throw new Error('Catalogue items could not be loaded');
      }

      setProducts(itemsResponse.data?.results || []);
    } catch (error) {
      toast({
        title: 'Unable to load catalogue',
        description:
          'We could not retrieve your catalogue items right now. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [orgId]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const filtered = products.filter((product) => {
      const matchesQuery =
        !normalizedQuery ||
        product.name.toLowerCase().includes(normalizedQuery) ||
        product.description?.toLowerCase().includes(normalizedQuery) ||
        String(product.price).includes(normalizedQuery);

      const imageCount = product.images?.length || 0;
      const matchesStatus =
        status === 'all' ||
        (status === 'active' && product.is_active) ||
        (status === 'inactive' && !product.is_active) ||
        (status === 'with-images' && imageCount > 0) ||
        (status === 'without-images' && imageCount === 0);

      return matchesQuery && matchesStatus;
    });

    return filtered.sort((first, second) => {
      switch (sort) {
        case 'name-asc':
          return first.name.localeCompare(second.name);
        case 'name-desc':
          return second.name.localeCompare(first.name);
        case 'price-high':
          return Number(second.price) - Number(first.price);
        case 'price-low':
          return Number(first.price) - Number(second.price);
        case 'updated':
        default:
          return (
            new Date(second.updated_at || second.created_at || 0).getTime() -
            new Date(first.updated_at || first.created_at || 0).getTime()
          );
      }
    });
  }, [products, query, sort, status]);

  const stats = useMemo(() => {
    const totalValue = products.reduce(
      (sum, product) => sum + Number(product.price || 0),
      0,
    );
    const activeCount = products.filter((product) => product.is_active).length;
    const mediaReadyCount = products.filter((product) => product.images?.length).length;

    return [
      { label: 'Items', value: String(products.length) },
      { label: 'Active', value: String(activeCount) },
      { label: 'With media', value: String(mediaReadyCount) },
      { label: 'Visible value', value: formatCurrency(totalValue) },
    ];
  }, [products]);

  const openItem = (itemId: string) => {
    if (!orgId || !catalogueId) {
      return;
    }

    navigate(`/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${itemId}`);
  };

  const editItem = (itemId: string) => {
    if (!orgId || !catalogueId) {
      return;
    }

    navigate(
      `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${itemId}/edit`,
    );
  };

  const openDelete = (item: CatalogueItem) => {
    setSelectedItem(item);
    onOpen();
  };

  const handleDelete = async () => {
    if (!orgId || !catalogueId || !selectedItem) {
      return;
    }

    const response = await deleteCatalogueItem(orgId, selectedItem.id, catalogueId);

    if (response.success) {
      setProducts((current) => current.filter((item) => item.id !== selectedItem.id));
      toast({
        title: 'Item deleted',
        description: `${selectedItem.name} was removed from the catalogue.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      setSelectedItem(null);
      return;
    }

    toast({
      title: 'Delete failed',
      description: 'We could not delete this item. Please try again.',
      status: 'error',
      duration: 4000,
      isClosable: true,
    });
  };

  const createManual = () => {
    if (!orgId || !catalogueId) {
      return;
    }
    navigate(`/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/new/form`);
  };

  const createFromText = () => {
    if (!orgId || !catalogueId) {
      return;
    }
    navigate(`/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/new/text-ai`);
  };

  const createFromImage = () => {
    if (!orgId || !catalogueId) {
      return;
    }
    navigate(`/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/new/image-ai`);
  };

  const createFromVoice = () => {
    if (!orgId || !catalogueId) {
      return;
    }
    navigate(`/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/new/voice`);
  };

  return (
    <CatalogueShell>
      <CatalogueHero
        eyebrow="Catalogue"
        title="Manage your catalogue items"
        description="View products, check prices, update images, and keep your catalogue ready for quotes, orders, and customer conversations."
        stats={stats}
        primaryAction={
          <PrimaryCatalogueButton
            onClick={createManual}
            isDisabled={!catalogueId}
          >
            Add item manually
          </PrimaryCatalogueButton>
        }
        secondaryAction={
          <Button
            variant="outline"
            borderRadius="16px"
            onClick={fetchProducts}
            isLoading={loading}
          >
            Refresh catalogue
          </Button>
        }
      />

      <CatalogueCommandBar
        query={query}
        onQueryChange={setQuery}
        sort={sort}
        onSortChange={setSort}
        status={status}
        onStatusChange={setStatus}
        view={view}
        onViewChange={setView}
        resultLabel={`${filteredProducts.length} of ${products.length} items shown`}
        addMenu={
          <CatalogueCreateMenu
            onCreateManual={createManual}
            onCreateFromText={createFromText}
            onCreateFromImage={createFromImage}
            onCreateFromVoice={createFromVoice}
            isDisabled={!catalogueId}
          />
        }
      />

      {loading ? (
        <CatalogueListSkeleton />
      ) : view === 'table' ? (
        <CatalogueTable
          products={filteredProducts}
          orgId={orgId}
          catalogueId={catalogueId}
          onOpenItem={openItem}
          onEditItem={editItem}
          onDeleteItem={openDelete}
        />
      ) : (
        <CatalogueCards
          products={filteredProducts}
          onOpenItem={openItem}
          onEditItem={editItem}
          onDeleteItem={openDelete}
        />
      )}

      {!loading && products.length > 0 ? (
        <Box mt={5}>
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <Text fontSize="sm" color="gray.500">
              Tip: use the filters to quickly find items that still need images or updates.
            </Text>
          </HStack>
        </Box>
      ) : null}

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDelete}
        isLoading={false}
        AreYouSureQuestion={`Delete ${selectedItem?.name || 'this item'}?`}
        AreYouSureTextExplanation="This removes the item from the catalogue and cannot be undone."
        confirmButtonText="Delete item"
        confirmButtonColor="#E53E3E"
        colorScheme="red"
      />
    </CatalogueShell>
  );
};

export default CataloguePage;
